import { useState } from "react";
import { Link } from "react-router-dom";

import { useAccounts, useConsoleConfig, useDatasets } from "../api/queries";
import type { ConsoleConfig, Dataset, GateState } from "../api/types";
import { isStorageProblem, StorageProblem } from "../components/StorageProblem";
import { PageHeader } from "../components/layout/PageHeader";
import {
  EmptyBlock,
  ErrorBlock,
  LoadingBlock,
} from "../components/ui/StateBlock";
import { formatDateTime, formatScore, pluralize } from "../lib/format";
import { metricLabel, PURPOSE_LABELS } from "../lib/metrics";

/** What each gate state is called on screen. It is never "failed". */
const GATE_LABELS: Record<GateState, string> = {
  no_bar: "no bar",
  bar_set: "bar set",
  bar_cleared: "bar cleared",
  not_yet_passed: "not yet passed",
};

const GATE_TONES: Record<GateState, string> = {
  no_bar: "neutral",
  bar_set: "pending",
  bar_cleared: "ok",
  not_yet_passed: "warning",
};

interface DatasetRowProps {
  dataset: Dataset;
  config: ConsoleConfig | undefined;
}

/**
 * One dataset in the catalogue.
 *
 * A row always carries the two things that make it a record rather than a
 * filename: whose data it is, and what we may do with it. There is no shape
 * of this component that can render without them.
 */
function DatasetRow({ dataset, config }: DatasetRowProps): JSX.Element {
  const permitted = dataset.consent.permissions;
  return (
    <li className="catalogue__row">
      <span className="catalogue__identity">
        <Link className="catalogue__name" to={`/datasets/${dataset.id}`}>
          {dataset.account.name} · {dataset.name}
        </Link>
        <span className="catalogue__meta">
          {dataset.versions.available > 0
            ? `${pluralize(dataset.versions.available, "file")} available of ${dataset.versions.total}`
            : `no file available yet, ${pluralize(dataset.versions.total, "version")} on record`}
          {dataset.versions.latest?.available_at
            ? ` · newest ${formatDateTime(dataset.versions.latest.available_at)}`
            : ""}
        </span>
        <span className="catalogue__permissions">
          {permitted.length > 0
            ? permitted
                .map((purpose) => PURPOSE_LABELS[purpose])
                .join(" · ")
            : "nothing is allowed on this data"}
        </span>
      </span>

      <span className="catalogue__gate">
        <span className="badge" data-tone={GATE_TONES[dataset.gate.state]}>
          {GATE_LABELS[dataset.gate.state]}
        </span>
        {dataset.gate.threshold_value !== null ? (
          <span className="catalogue__meta">
            beat {formatScore(dataset.gate.threshold_value)}{" "}
            {metricLabel(config, dataset.task_type, dataset.gate.metric)}
            {dataset.gate.best
              ? ` · best ${formatScore(dataset.gate.best.value)}`
              : ""}
          </span>
        ) : null}
        {dataset.deleted_at ? (
          <span className="catalogue__meta">
            The file was erased; the record is kept.
          </span>
        ) : null}
      </span>
    </li>
  );
}

/**
 * Which clients we hold data for, and where each deal stands.
 *
 * The first screen: a person opens this product to find a client's file and to
 * see whether the bar has been cleared, so both are here before anything else.
 *
 * Returns:
 *     The catalogue.
 */
export function AccountsPage(): JSX.Element {
  const [account, setAccount] = useState<string>("");
  const accounts = useAccounts();
  const datasets = useDatasets(account || undefined);
  const config = useConsoleConfig();

  const header = (
    <PageHeader
      eyebrow="Clients"
      title="Whose data we hold"
      subtitle="Every file a client gave us, whose it is, what we may do with it, and what we have to beat."
      actions={
        <Link className="btn btn--primary" to="/new">
          Add a dataset
        </Link>
      }
    />
  );

  if (accounts.isPending || datasets.isPending) {
    return (
      <>
        {header}
        <LoadingBlock label="Reading the catalogue…" />
      </>
    );
  }

  const error = accounts.error ?? datasets.error;
  if (isStorageProblem(error)) {
    return (
      <>
        {header}
        <StorageProblem
          error={error}
          onRetry={() => {
            void accounts.refetch();
            void datasets.refetch();
          }}
        />
      </>
    );
  }

  if (accounts.isError || datasets.isError) {
    return (
      <>
        {header}
        <ErrorBlock
          error={error}
          onRetry={() => {
            void accounts.refetch();
            void datasets.refetch();
          }}
        />
      </>
    );
  }

  return (
    <>
      {header}

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">
            {accounts.data.items.length === 0
              ? "No client accounts yet"
              : `${pluralize(accounts.data.items.length, "client")}`}
          </h2>
          {accounts.data.items.length > 0 ? (
            <label className="inline-field">
              <span className="inline-field__label">show</span>
              <select
                className="select select--inline"
                value={account}
                onChange={(event) => setAccount(event.target.value)}
                aria-label="Which client to show"
              >
                <option value="">every client</option>
                {accounts.data.items.map((item) => (
                  <option key={item.id} value={item.slug}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        {accounts.data.items.length === 0 ? (
          <EmptyBlock label="Nobody has added a client yet. Adding a dataset is where the first one is created." />
        ) : (
          <ul className="accounts">
            {accounts.data.items.map((item) => (
              <li className="accounts__row" key={item.id}>
                <button
                  type="button"
                  className="accounts__name"
                  data-selected={account === item.slug}
                  onClick={() =>
                    setAccount((current) =>
                      current === item.slug ? "" : item.slug,
                    )
                  }
                >
                  {item.name}
                </button>
                <span className="accounts__meta mono">{item.slug}</span>
                <span className="accounts__meta">
                  {pluralize(item.datasets, "dataset")} ·{" "}
                  {item.versions_available} available
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">Datasets</h2>
          <span className="section__meta">
            {datasets.data.scope
              ? `narrowed to ${datasets.data.scope.join(", ")}`
              : "newest first"}
          </span>
        </div>

        {datasets.data.items.length === 0 ? (
          <EmptyBlock
            label={
              account
                ? "This client has no dataset on the record yet."
                : "No dataset has been added yet. The first one takes four questions."
            }
          />
        ) : (
          <ul className="catalogue">
            {datasets.data.items.map((dataset) => (
              <DatasetRow
                key={dataset.id}
                dataset={dataset}
                config={config.data}
              />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
